import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Zap, CheckCircle2, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const INDUSTRIES = [
  ["exterior_cleaning", "Exterior cleaning"],
  ["pressure_washing", "Pressure washing"],
  ["hvac", "HVAC"],
  ["plumbing", "Plumbing"],
  ["roofing", "Roofing"],
  ["landscaping", "Landscaping"],
  ["auto_dealership", "Auto dealership"],
  ["other", "Other"],
];

/** Public beta-waitlist form. Replaces the public /register path.
 *  Honeypot: `website_url_hp` is CSS-hidden; any value → server silently
 *  drops the request. Rate-limited to 5/hour/IP in the API. */
export default function RequestAccess() {
  const nav = useNavigate();
  const [f, setF] = useState({
    full_name: "", business_name: "", email: "", phone: "",
    industry: "", website: "", employees: "", needs: "",
    website_url_hp: "", // honeypot
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.industry) { toast.error("Please pick an industry"); return; }
    setBusy(true);
    try {
      await api.post("/public/access-requests", f);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(formatApiErrorDetail(d) || "Could not submit — try again in a bit.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <Toaster richColors position="top-right"/>
        <div className="bg-white border border-zinc-200 rounded-2xl card-crisp max-w-lg w-full p-10 text-center" data-testid="request-access-thank-you">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-7 h-7 text-emerald-700"/>
          </div>
          <h1 className="font-heading text-3xl font-bold mt-5">You're on the list.</h1>
          <p className="text-zinc-600 mt-3 leading-relaxed">
            Thanks — you're on the LeadForge beta list. We'll be in touch personally.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Button variant="outline" onClick={() => nav("/")}>Back to homepage</Button>
            <Button className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" onClick={() => nav("/login")}>
              I already have an account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster richColors position="top-right"/>
      <div className="min-h-screen bg-zinc-50 grid md:grid-cols-2">
        <div className="hidden md:flex sidebar-dark flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[#FF5A00] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" strokeWidth={2.5}/>
            </div>
            <span className="font-heading font-bold text-xl">LeadForge</span>
          </div>
          <div>
            <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-4">Private beta · Invite only</div>
            <h1 className="font-heading text-5xl font-black tracking-tight leading-tight">
              Request access.
            </h1>
            <p className="mt-6 text-zinc-400 max-w-md">
              We're onboarding a handful of home-service operators by hand so every workspace gets personally tuned. Tell us a bit about your business and we'll be in touch.
            </p>
          </div>
          <div className="text-xs text-zinc-500 font-mono-accent">© 2026 LEADFORGE</div>
        </div>

        <div className="flex items-start justify-center p-8 overflow-y-auto">
          <div className="w-full max-w-md py-6">
            <h2 className="font-heading text-3xl font-bold">Join the beta</h2>
            <p className="text-zinc-500 mt-2">We review every request personally.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="request-access-form">
              {/* HONEYPOT — hidden from users, visible to bots. Any value → server drops silently. */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                <label>Website URL
                  <input tabIndex={-1} autoComplete="off" value={f.website_url_hp}
                            onChange={(e)=>set("website_url_hp", e.target.value)}/>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Full name</Label>
                  <Input required value={f.full_name} onChange={(e)=>set("full_name", e.target.value)} data-testid="ra-full-name"/>
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input required value={f.phone} onChange={(e)=>set("phone", e.target.value)} data-testid="ra-phone"/>
                </div>
              </div>
              <div>
                <Label>Business name</Label>
                <Input required value={f.business_name} onChange={(e)=>set("business_name", e.target.value)} data-testid="ra-business-name"/>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required value={f.email} onChange={(e)=>set("email", e.target.value)} data-testid="ra-email"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Industry / trade</Label>
                  <Select value={f.industry} onValueChange={(v)=>set("industry", v)}>
                    <SelectTrigger data-testid="ra-industry"><SelectValue placeholder="Pick one"/></SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label># of employees</Label>
                  <Input required value={f.employees} onChange={(e)=>set("employees", e.target.value)} placeholder="e.g. 3" data-testid="ra-employees"/>
                </div>
              </div>
              <div>
                <Label>Website <span className="text-xs text-zinc-400">(optional)</span></Label>
                <Input value={f.website} onChange={(e)=>set("website", e.target.value)} placeholder="https://" data-testid="ra-website"/>
              </div>
              <div>
                <Label>What do you want LeadForge to help with?</Label>
                <Textarea required rows={4} value={f.needs} onChange={(e)=>set("needs", e.target.value)}
                            placeholder="e.g. we're losing leads from Facebook messages at night…"
                            data-testid="ra-needs"/>
              </div>
              <Button type="submit" disabled={busy}
                        className="w-full h-11 bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
                        data-testid="ra-submit">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-1"/> Sending…</> : "Request access"}
              </Button>
            </form>
            <p className="mt-6 text-sm text-zinc-500">
              Already have an account? <Link to="/login" className="text-[#FF5A00] font-medium hover:underline">Log in</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
